import { useId } from "react";
import classNames from "classnames";

import styles from "./UI.module.css";

/**
 * Replaces MUI's outlined <TextField>, reproducing its DOM so the geometry matches:
 * an absolutely-positioned <fieldset> carries the border (keeping the control 56px
 * tall) and a real <legend> forms the notch the floating label sits in.
 *
 * The float is CSS-only — the input always carries `placeholder=" "`, so
 * `:not(:placeholder-shown)` means "has a value". Emotion's version needed a
 * runtime <style>, which `style-src 'self'` blocks.
 */
export default function TextField({
    className,
    disabled = false,
    endAdornment,
    error = false,
    fullWidth = false,
    helperText,
    inputProps,
    label,
    ...rest
}) {
    const inputId = useId();

    return (
        <div
            className={classNames(
                styles.field,
                fullWidth && styles.fieldFullWidth,
                error && styles.fieldError,
                disabled && styles.fieldDisabled,
                className,
            )}
        >
            <div
                className={classNames(styles.root, {
                    [styles.rootAdorned]: !!endAdornment,
                })}
            >
                <input
                    className={styles.input}
                    disabled={disabled}
                    id={inputId}
                    placeholder=" "
                    {...inputProps}
                    {...rest}
                />

                {endAdornment ? (
                    <div className={styles.adornment}>{endAdornment}</div>
                ) : null}

                <label className={styles.label} htmlFor={inputId}>
                    {label}
                </label>

                <fieldset aria-hidden="true" className={styles.outline}>
                    <legend className={styles.legend}>
                        <span>{label}</span>
                    </legend>
                </fieldset>
            </div>

            {helperText ? (
                <p className={styles.helperText}>{helperText}</p>
            ) : null}
        </div>
    );
}
